import { useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { api } from "./api";
import type {
  ActivityRow,
  AccountRow,
  AuditRow,
  BudgetRow,
  CorrectionRow,
  DeviceRow,
  ErrorRow,
  ExpenseRow,
  FeedbackDetailResponse,
  FeedbackListResponse,
  GoalRow,
  IncomeRow,
  MessageRow,
  NotificationRow,
  OverviewResponse,
  PaginatedResponse,
  RecurringRow,
  SavingsRow,
  SessionRow,
  SubscriptionDetailResponse,
  SubscriptionEventsResponse,
  SubscriptionStatsResponse,
  SubscriptionsListResponse,
  SystemHealthResponse,
  TripRow,
  UserFullResponse,
  UsersMapResponse,
  UsersListResponse,
} from "@/types";

export const qk = {
  overview: ["overview"] as const,
  users: (params: Record<string, unknown>) => ["users", params] as const,
  usersMap: (params: Record<string, unknown>) => ["users", "map", params] as const,
  user: (id: string) => ["user", id] as const,
  userFull: (id: string) => ["user", id, "full"] as const,
  userExpenses: (id: string, params: Record<string, unknown>) => ["user", id, "expenses", params] as const,
  userMessages: (id: string, params: Record<string, unknown>) => ["user", id, "messages", params] as const,
  userErrors: (id: string, params: Record<string, unknown>) => ["user", id, "errors", params] as const,
  userActivity: (id: string, params: Record<string, unknown>) => ["user", id, "activity", params] as const,
  userNotifications: (id: string, params: Record<string, unknown>) => ["user", id, "notifications", params] as const,
  userDevices: (id: string) => ["user", id, "devices"] as const,
  userSessions: (id: string) => ["user", id, "sessions"] as const,
  userAccounts: (id: string) => ["user", id, "accounts"] as const,
  userRecurring: (id: string) => ["user", id, "recurring"] as const,
  userIncome: (id: string) => ["user", id, "income"] as const,
  userBudgets: (id: string) => ["user", id, "budgets"] as const,
  userGoals: (id: string) => ["user", id, "goals"] as const,
  userTrips: (id: string) => ["user", id, "trips"] as const,
  userSavings: (id: string) => ["user", id, "savings"] as const,
  userCorrections: (id: string) => ["user", id, "corrections"] as const,
  feedback: (params: Record<string, unknown>) => ["feedback", params] as const,
  feedbackDetail: (id: number | string) => ["feedback", id] as const,
  subscriptions: (params: Record<string, unknown>) => ["subscriptions", params] as const,
  subscriptionStats: ["subscriptions", "stats"] as const,
  subscriptionEvents: (params: Record<string, unknown>) => ["subscription-events", params] as const,
  subscriptionDetail: (id: string) => ["subscription", id] as const,
  errors: (params: Record<string, unknown>) => ["errors", params] as const,
  activity: (params: Record<string, unknown>) => ["activity", params] as const,
  messages: (params: Record<string, unknown>) => ["messages", params] as const,
  audit: (params: Record<string, unknown>) => ["audit", params] as const,
  health: ["system", "health"] as const,
};

type QO<T> = Omit<UseQueryOptions<T>, "queryKey" | "queryFn">;

export function useOverview(opts?: QO<OverviewResponse>) {
  return useQuery({
    queryKey: qk.overview,
    queryFn: ({ signal }) => api.get<OverviewResponse>("/api/admin/overview", undefined, signal),
    staleTime: 30_000,
    ...opts,
  });
}

export function useUsers(params: { limit?: number; offset?: number; search?: string; sort?: string; dir?: string }, opts?: QO<UsersListResponse>) {
  return useQuery({
    queryKey: qk.users(params),
    queryFn: ({ signal }) => api.get<UsersListResponse>("/api/admin/users", params, signal),
    staleTime: 10_000,
    ...opts,
  });
}

export function useUsersMap(params: { limit?: number; search?: string } = {}, opts?: QO<UsersMapResponse>) {
  return useQuery({
    queryKey: qk.usersMap(params),
    queryFn: ({ signal }) => api.get<UsersMapResponse>("/api/admin/users/map", params, signal),
    staleTime: 60_000,
    ...opts,
  });
}

export function useUserFull(id: string, opts?: QO<UserFullResponse>) {
  return useQuery({
    queryKey: qk.userFull(id),
    queryFn: ({ signal }) => api.get<UserFullResponse>(`/api/admin/users/${id}/full`, undefined, signal),
    enabled: !!id,
    staleTime: 10_000,
    ...opts,
  });
}

function makeListHook<T>(buildPath: (id: string) => string, key: (id: string, p: Record<string, unknown>) => readonly unknown[]) {
  return function useList(id: string, params: Record<string, unknown> = {}, opts?: QO<PaginatedResponse<T>>) {
    return useQuery({
      queryKey: key(id, params),
      queryFn: ({ signal }) => api.get<PaginatedResponse<T>>(buildPath(id), params, signal),
      enabled: !!id,
      staleTime: 10_000,
      ...opts,
    });
  };
}

export const useUserExpenses = makeListHook<ExpenseRow>((id) => `/api/admin/users/${id}/expenses`, qk.userExpenses);
export const useUserMessages = makeListHook<MessageRow>((id) => `/api/admin/users/${id}/messages`, qk.userMessages);
export const useUserErrors = makeListHook<ErrorRow>((id) => `/api/admin/users/${id}/errors`, qk.userErrors);
export const useUserActivity = makeListHook<ActivityRow>((id) => `/api/admin/users/${id}/activity`, qk.userActivity);
export const useUserNotifications = makeListHook<NotificationRow>((id) => `/api/admin/users/${id}/notifications`, qk.userNotifications);

export function useUserDevices(id: string) {
  return useQuery({
    queryKey: qk.userDevices(id),
    queryFn: ({ signal }) => api.get<PaginatedResponse<DeviceRow>>(`/api/admin/users/${id}/devices`, undefined, signal),
    enabled: !!id,
  });
}

export function useUserSessions(id: string) {
  return useQuery({
    queryKey: qk.userSessions(id),
    queryFn: ({ signal }) => api.get<PaginatedResponse<SessionRow>>(`/api/admin/users/${id}/sessions`, undefined, signal),
    enabled: !!id,
  });
}

export function useUserAccounts(id: string) {
  return useQuery({
    queryKey: qk.userAccounts(id),
    queryFn: ({ signal }) => api.get<PaginatedResponse<AccountRow>>(`/api/admin/users/${id}/accounts`, undefined, signal),
    enabled: !!id,
  });
}

export function useUserRecurring(id: string) {
  return useQuery({
    queryKey: qk.userRecurring(id),
    queryFn: ({ signal }) => api.get<PaginatedResponse<RecurringRow>>(`/api/admin/users/${id}/recurring`, undefined, signal),
    enabled: !!id,
  });
}

export function useUserIncome(id: string) {
  return useQuery({
    queryKey: qk.userIncome(id),
    queryFn: ({ signal }) => api.get<PaginatedResponse<IncomeRow>>(`/api/admin/users/${id}/income`, undefined, signal),
    enabled: !!id,
  });
}

export function useUserBudgets(id: string) {
  return useQuery({
    queryKey: qk.userBudgets(id),
    queryFn: ({ signal }) => api.get<PaginatedResponse<BudgetRow>>(`/api/admin/users/${id}/budgets`, undefined, signal),
    enabled: !!id,
  });
}

export function useUserGoals(id: string) {
  return useQuery({
    queryKey: qk.userGoals(id),
    queryFn: ({ signal }) => api.get<PaginatedResponse<GoalRow>>(`/api/admin/users/${id}/goals`, undefined, signal),
    enabled: !!id,
  });
}

export function useUserTrips(id: string) {
  return useQuery({
    queryKey: qk.userTrips(id),
    queryFn: ({ signal }) => api.get<PaginatedResponse<TripRow>>(`/api/admin/users/${id}/trips`, undefined, signal),
    enabled: !!id,
  });
}

export function useUserSavings(id: string) {
  return useQuery({
    queryKey: qk.userSavings(id),
    queryFn: ({ signal }) => api.get<PaginatedResponse<SavingsRow>>(`/api/admin/users/${id}/savings`, undefined, signal),
    enabled: !!id,
  });
}

export function useUserCorrections(id: string) {
  return useQuery({
    queryKey: qk.userCorrections(id),
    queryFn: ({ signal }) => api.get<PaginatedResponse<CorrectionRow>>(`/api/admin/users/${id}/corrections`, undefined, signal),
    enabled: !!id,
  });
}

export function useErrors(
  params: { limit?: number; offset?: number; path?: string; user_id?: string; since?: string },
  opts?: QO<PaginatedResponse<ErrorRow>>
) {
  return useQuery({
    queryKey: qk.errors(params),
    queryFn: ({ signal }) => api.get<PaginatedResponse<ErrorRow>>("/api/admin/errors", params, signal),
    staleTime: 10_000,
    ...opts,
  });
}

export function useActivity(
  params: { limit?: number; offset?: number; path?: string; user_id?: string; status?: string; since?: string },
  opts?: QO<PaginatedResponse<ActivityRow>>
) {
  return useQuery({
    queryKey: qk.activity(params),
    queryFn: ({ signal }) => api.get<PaginatedResponse<ActivityRow>>("/api/admin/activity", params, signal),
    staleTime: 5_000,
    ...opts,
  });
}

export function useMessages(
  params: { limit?: number; offset?: number; channel?: string; user_id?: string },
  opts?: QO<PaginatedResponse<MessageRow>>
) {
  return useQuery({
    queryKey: qk.messages(params),
    queryFn: ({ signal }) => api.get<PaginatedResponse<MessageRow>>("/api/admin/messages", params, signal),
    staleTime: 10_000,
    ...opts,
  });
}

export function useFeedback(
  params: {
    limit?: number;
    offset?: number;
    status?: string;
    kind?: string;
    priority?: string;
    user_id?: string;
    search?: string;
  },
  opts?: QO<FeedbackListResponse>
) {
  return useQuery({
    queryKey: qk.feedback(params),
    queryFn: ({ signal }) => api.get<FeedbackListResponse>("/api/admin/feedback", params, signal),
    staleTime: 5_000,
    ...opts,
  });
}

export function useFeedbackDetail(id: number | string | null, opts?: QO<FeedbackDetailResponse>) {
  return useQuery({
    queryKey: qk.feedbackDetail(id ?? "none"),
    queryFn: ({ signal }) => api.get<FeedbackDetailResponse>(`/api/admin/feedback/${id}`, undefined, signal),
    enabled: id != null,
    staleTime: 5_000,
    ...opts,
  });
}

export function useAudit(params: { limit?: number; offset?: number }, opts?: QO<PaginatedResponse<AuditRow>>) {
  return useQuery({
    queryKey: qk.audit(params),
    queryFn: ({ signal }) => api.get<PaginatedResponse<AuditRow>>("/api/admin/audit", params, signal),
    staleTime: 10_000,
    ...opts,
  });
}

export function useSystemHealth() {
  return useQuery({
    queryKey: qk.health,
    queryFn: ({ signal }) => api.get<SystemHealthResponse>("/api/admin/system/health", undefined, signal),
    staleTime: 15_000,
  });
}

export function useSubscriptions(
  params: { limit?: number; offset?: number; status?: string; environment?: string; store?: string; search?: string },
  opts?: QO<SubscriptionsListResponse>
) {
  return useQuery({
    queryKey: qk.subscriptions(params),
    queryFn: ({ signal }) => api.get<SubscriptionsListResponse>("/api/admin/subscriptions", params, signal),
    staleTime: 10_000,
    ...opts,
  });
}

export function useSubscriptionStats(opts?: QO<SubscriptionStatsResponse>) {
  return useQuery({
    queryKey: qk.subscriptionStats,
    queryFn: ({ signal }) => api.get<SubscriptionStatsResponse>("/api/admin/subscriptions/stats", undefined, signal),
    staleTime: 30_000,
    ...opts,
  });
}

export function useSubscriptionEvents(
  params: { limit?: number; offset?: number; type?: string; environment?: string; processed?: string; trigger?: string; search?: string },
  opts?: QO<SubscriptionEventsResponse>
) {
  return useQuery({
    queryKey: qk.subscriptionEvents(params),
    queryFn: ({ signal }) => api.get<SubscriptionEventsResponse>("/api/admin/subscriptions/events", params, signal),
    staleTime: 5_000,
    ...opts,
  });
}

export function useSubscriptionDetail(userId: string | null, opts?: QO<SubscriptionDetailResponse>) {
  return useQuery({
    queryKey: qk.subscriptionDetail(userId ?? "none"),
    queryFn: ({ signal }) => api.get<SubscriptionDetailResponse>(`/api/admin/subscriptions/${userId}`, undefined, signal),
    enabled: userId != null,
    staleTime: 5_000,
    ...opts,
  });
}

export function useInvalidateUser() {
  const qc = useQueryClient();
  return (id: string) => {
    qc.invalidateQueries({ queryKey: ["user", id] });
    qc.invalidateQueries({ queryKey: ["users"] });
  };
}

// Helper for paginated payload normalization
export function paginatedItems<T>(p: PaginatedResponse<T> | undefined): T[] {
  if (!p) return [];
  return p.items ?? p.rows ?? p.data ?? [];
}
